var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { User } from "./user";
import { CsrfToken } from "./csrf";
import { URLParams, Redirect, isDefined, LocalStorage, Cookie } from "./utils";
/**
 * A class for Cognito SSO authentication and authorization operations.
 */
var CognitoAuthService = /** @class */ (function () {
    /**
     * Instantiates a Cognito auth service
     * @param authConfig - The auth configuration details
     */
    function CognitoAuthService(authConfig) {
        var _this = this;
        /**
         * Asynchronous function that executes token exchange for an
         * authenticated user and sets short-lived authorization cookies
         * and CloudFront signed cookies.
         */
        this.authorizeUser = function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.user.authorizeUser(this.getAuthorizationCode(), this.authConfig.appClientId, this.authConfig.appLoginUrl, this.authConfig.tokenUrl, this.authConfig.commonDomain)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Redirects user to identity provider's authentication service.
         */
        this.authenticateUser = function () {
            _this.user.authenticateUser(_this.authConfig.appClientId, _this.authConfig.appLoginUrl, _this.authConfig.identityProvider, _this.authConfig.ssoLoginUrl);
        };
        /**
         * Asynchronous function that refreshes user tokens.
         */
        this.refreshUserTokens = function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.user.refreshUserTokens(this.authConfig.appClientId, this.authConfig.tokenUrl);
                return [2 /*return*/];
            });
        }); };
        /**
         * Asynchronous function that validates user tokens and updates the
         * user's authorization status in local storage.
         */
        this.isUserAuthorized = function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.user.isUserAuthorized(this.authConfig.userPoolId, this.authConfig.appClientId)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Reads authorization cookies from newly logged in user and writes them to
         * local storage.
         * @returns an object containing user authorization items.
         */
        this.loadUserSession = function () {
            _this.setUserAuthorizationItems();
            var userAuthorizationItems = _this.getUserAuthorizationItems();
            if (Object.entries(userAuthorizationItems).length === 0) {
                throw new Error("Error loading user authorization items");
            }
            return userAuthorizationItems;
        };
        /**
         * Reads user authorization items from local storage.
         * @returns an object containing user authorization items.
         */
        this.getUserAuthorizationItems = function () {
            return _this.user.getUserAuthorizationItemsFromLocalStorage();
        };
        /**
         * Reads user authorization items from cookies and writes them
         * to local storage.
         */
        this.setUserAuthorizationItems = function () {
            var userAuthorizationItems = _this.user.readUserAuthorizationCookies();
            if (Object.entries(userAuthorizationItems).length > 0) {
                LocalStorage.setItem("userAuthorizationItems", JSON.stringify(userAuthorizationItems));
            }
        };
        /**
         * Sets CloudFront signed cookies in the browser for an authorized user.
         */
        this.setCloudFrontSignedCookies = function () { return __awaiter(_this, void 0, void 0, function () {
            var userAuthorizationItems;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userAuthorizationItems = this.getUserAuthorizationItems();
                        return [4 /*yield*/, Cookie.setCloudFrontSignedCookies(this.authConfig.cookieSignerUrl, userAuthorizationItems.userTokens.accessToken)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Removes user authorization items from local storage and redirects
         * to login site.
         */
        this.logoutUser = function () {
            _this.user.invalidateUser();
            _this.redirectToLandingApp();
        };
        /**
         * Redirects to protected app.
         */
        this.redirectToMainApp = function () {
            Redirect.redirect(_this.authConfig.appMainUrl);
        };
        /**
         * Redirects to landing app.
         */
        this.redirectToLandingApp = function () {
            Redirect.redirect(_this.authConfig.appLoginUrl);
        };
        /**
         * Checks if the user has been redirected from the identity provider with an
         * access code.
         * @returns A boolean indicating whether the user was redirected from the authorization server.
         */
        this.isRedirectFromIdentityProvider = function () {
            var authorizationCode = _this.getAuthorizationCode();
            if (!isDefined(authorizationCode)) {
                return false;
            }
            if (!_this.isValidOAuthStateToken()) {
                throw new Error("Invalid OAuth state");
            }
            return true;
        };
        /**
         * Validates the randomized state token.
         * @returns A boolean indicating whether the token is valid or invalid.
         */
        this.isValidOAuthStateToken = function () {
            return CsrfToken.isValidStateToken(URLParams.get("state"));
        };
        /**
         * Retruns the user's authorization code.
         * @returns The authorization code.
         */
        this.getAuthorizationCode = function () {
            return URLParams.get("code");
        };
        this.authConfig = authConfig;
        this.user = new User();
    }
    return CognitoAuthService;
}());
export { CognitoAuthService };
